import { Link } from "@inertiajs/react";
import { useEffect } from "react";
import Layout from "@/components/Layout";
import PageAuditHistory from "@/components/pages/PageAuditHistory";
import PageIssueGroup from "@/components/pages/PageIssueGroup";
import type { PageAudit, PageDetail, PageDetailDomain, PageIssueGroups, PageIssuePriority } from "@/types";

type ShowProps = {
  domain: PageDetailDomain;
  page: PageDetail;
  issue_groups: PageIssueGroups;
  audits: PageAudit[];
};

const priorityOrder: PageIssuePriority[] = ["high", "medium", "low"];

export default function Show({ domain, page, issue_groups, audits }: ShowProps) {
  useEffect(() => {
    const id = window.location.hash.slice(1);
    if (!id) return;

    const frame = requestAnimationFrame(() => {
      document.getElementById(id)?.scrollIntoView({ block: "start" });
    });

    return () => cancelAnimationFrame(frame);
  }, [page.id]);

  return (
    <Layout title={page.title || page.path}>
      <div className="mb-6">
        <Link href={domain.domain_url} className="mb-3 inline-block text-sm text-primary hover:underline">
          Back to {domain.hostname}
        </Link>
        <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-start">
          <div>
            <h1 className="mb-1 text-2xl font-semibold">{page.title || page.path}</h1>
            <a
              href={page.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-500 hover:text-primary hover:underline"
            >
              {page.url}
            </a>
          </div>
          <div className="rounded-lg bg-white px-4 py-3 text-sm shadow-sm">
            <span className="font-medium">{page.issue_count}</span> {page.issue_count === 1 ? "issue" : "issues"}
          </div>
        </div>
      </div>

      <div className="space-y-8">
        {page.issue_count === 0 ? (
          <div className="card">
            <div className="card-body py-10 text-center">
              <h2 className="mb-2 text-xl font-semibold text-gray-900">No issues found for this page</h2>
              <p className="text-gray-500">Scout has not found page-level usability issues here yet.</p>
            </div>
          </div>
        ) : (
          priorityOrder.map((priority) => (
            <PageIssueGroup key={priority} priority={priority} issues={issue_groups[priority] || []} />
          ))
        )}

        <PageAuditHistory audits={audits} />
      </div>
    </Layout>
  );
}
